import PropTypes from "prop-types";
import React, { useCallback } from "react";
import { Button, Title } from "@yoast/ui-library";
import { FormattedMessage, injectIntl, defineMessages } from "react-intl";
import { BackButton } from "../BackButton";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";

import * as styles from "./styles.module.scss";

const SITE_TYPE_ADMIN_PANELS = {
	wordpress: "WordPress admin",
	shopify: "Shopify admin",
};

const SITE_TYPE_OPTIONS = {
	wordpress: {
		value: "wordpress",
		label: "WordPress",
	},
	typo3: {
		value: "typo3",
		label: "TYPO3",
	},
	shopify: {
		value: "shopify",
		label: "Shopify",
	},
};

const messages = defineMessages( {
	provisionerInfo: {
		id: "site.details.provisioner_info",
		defaultMessage: "Managed by: {provisioner}",
	},
	siteTypeInfo: {
		id: "site.details.site_type_info",
		defaultMessage: "Powered by: {siteType}",
	},
} );

/**
 * The SiteHeader component.
 *
 * @param {Object} props The props to use.
 *
 * @returns {ReactElement} The rendered component
 * @constructor
 */
function SiteHeader( props ) {
	const showAdminButton = [ "wordpress", "shopify" ].includes( props.siteType );
	const cmsAdminPanel = SITE_TYPE_ADMIN_PANELS[ props.siteType ];

	const adminLink = useCallback( siteUrl => {
		// Replace trailing slashes from the end of the site url.
		siteUrl = siteUrl.replace( /\/+$/, "" );

		switch ( props.siteType ) {
			case "wordpress":
				return `${ siteUrl }/wp-admin`;
			case "shopify":
				return `${ siteUrl }/admin/apps/yoast-seo-2`;
			default:
				return "";
		}
	}, [ props.siteType ] );

	return (
		<div className={ styles.siteHeader }>
			<div className={ styles.siteName }>
				<Title size="2">
					{ props.name }
				</Title>
				<div className="yst-flex yst-gap-4 yst-mt-2">
					{ props.provisioner && (
						<span>
							{ props.intl.formatMessage( messages.provisionerInfo, { provisioner: props.provisioner.displayName } ) }
						</span>
					) }
					{ props.siteType && SITE_TYPE_OPTIONS?.[ props.siteType ] && (
						<span>
							{ props.intl.formatMessage( messages.siteTypeInfo, { siteType: SITE_TYPE_OPTIONS[ props.siteType ].label } ) }
						</span>
					) }
				</div>
			</div>
			<div className={ styles.buttonArea }>
				<BackButton />
				{ showAdminButton &&
					<Button
						as="a"
						href={ adminLink( props.url ) }  target="_blank"
						type="button"
						variant="secondary"
					>
						<FormattedMessage id="sites.buttons.visitWp" defaultMessage="Open {cmsAdminPanel}" values={ { cmsAdminPanel } } />
						<ArrowTopRightOnSquareIcon />
					</Button>
				}
			</div>
		</div>
	);
}

export default injectIntl( SiteHeader );

SiteHeader.defaultProps = {
	provisioner: null,
};
SiteHeader.propTypes = {
	name: PropTypes.string.isRequired,
	url: PropTypes.string.isRequired,
	siteType: PropTypes.string.isRequired,
	provisioner: PropTypes.object,
	intl: PropTypes.object.isRequired,
};
